import axios from "axios";
import { toast } from "react-toastify";

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_ADMIN_API,
  withCredentials: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
});

export const fetchCsrfToken = async () => {
  try {
    const { data } = await api.get('/csrf-token');
    if (data?.csrfToken) {
      api.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
    }
  } catch (error) {
    console.error("Failed to fetch CSRF token:", error);
  }
};

// Immediately fetch CSRF token on module import
fetchCsrfToken();

// No Authorization header needed; rely on httpOnly cookies
api.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let pendingRequests = [];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // CSRF token missing/invalid: fetch fresh token and retry once
    if ((error.response?.status === 403 || error.response?.status === 419) && !originalRequest._csrfRetry) {
      originalRequest._csrfRetry = true;
      try {
        const { data } = await api.get('/csrf-token');
        if (data?.csrfToken) {
          api.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
          originalRequest.headers['X-CSRF-Token'] = data.csrfToken;
        }
        return api(originalRequest);
      } catch (e) {
        // fall through to 401 handling or reject
      }
    }

    // 401 Unauthorized: attempt refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Ensure CSRF token before refresh
          try {
            const { data } = await api.get('/csrf-token');
            if (data?.csrfToken) {
              api.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
            }
          } catch (_) {}

          await api.post('/admin/refresh'); // adjust refresh endpoint if needed
          isRefreshing = false;
          pendingRequests.forEach((cb) => cb());
          pendingRequests = [];
          return api(originalRequest);
        } catch (refreshErr) {
          isRefreshing = false;
          pendingRequests = [];
          if (typeof window !== 'undefined') {
            window.location.replace('/login');
          }
          return Promise.reject(refreshErr);
        }
      }

      return new Promise((resolve) => {
        pendingRequests.push(() => resolve(api(originalRequest)));
      });
    }

    // 403 Forbidden: show toast warning
    if (error.response?.status === 403) {
      toast.warning("You don't have permission to access this resource.");
    }

    return Promise.reject(error);
  }
);

export default api;
